import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Radio,
  Button,
  TextField,
  alpha,
  useTheme,
  IconButton,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Card from "../../common/Card";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { tokens } from "../../../theme";
import { useBranding } from "../../../BrandingContext";
import { useLocation, useNavigate } from "react-router-dom";
import { generate2faOTP, generate2faOTPForNonLoggedInUsers, TwoFactorSaveRequest } from "../../../redux/slice/2FA/twoFactorSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { ArrowBackRounded } from "@mui/icons-material";

type Method = "email" | "sms";

type SetupTwoFactorForm = {
  contactName: string;
};

export default function SetupTwoFactor() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const createPracticeAndUserRequest = location.state?.createPracticeAndUserRequest;
  const [method, setMethod] = React.useState<Method>("email");
  const dispatch = useAppDispatch();
  const { control, handleSubmit, setValue } = useForm<SetupTwoFactorForm>({
    defaultValues: {
      contactName: createPracticeAndUserRequest?.contactEmail ?? "",
    },
  });

  const handleMethodChange = (newMethod: Method) => {
    setMethod(newMethod);
    setValue(
      "contactName",
      newMethod === "email"
        ? createPracticeAndUserRequest?.contactEmail ?? ""
        : createPracticeAndUserRequest?.contactPhone ?? ""
    );
  };

  const logoSrc =
    theme.palette.mode === "dark"
      ? branding?.logoWhite
      : branding?.logo || require("../../../branding-assets/v3biomed/logo.png");

  const onSubmit = (data: SetupTwoFactorForm) => {
    generateOTP(data.contactName);

    navigate("/verify_two_factor", {
      state: {
        method,
        contact: data.contactName,
        createPracticeAndUserRequest
      },
    });
  };

  const generateOTP = async (contactValue: string) => {
    let obj: TwoFactorSaveRequest = {
      communicationValue: contactValue,
      communicationType: method === "email" ? "EMAIL" : "MOBILE"
    };
    await dispatch(generate2faOTPForNonLoggedInUsers({ request: obj }));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
      }}
    >
      <Box>
        <Box display="flex" justifyContent="center" mb={3}>
          <Box
            component="img"
            src={logoSrc}
            alt="V3 Biomed"
            sx={{
              height: 64,
              maxWidth: "70%",
              objectFit: "contain",
            }}
          />
        </Box>
 
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ width: "100%", maxWidth: 520, position: "relative" }}>
            <Button
              onClick={() => navigate("/create_account_step", { state: { createPracticeAndUserRequest } })}
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                color: "#2dd4bf",
              }}
            >
              <ArrowBackRounded/> Back
            </Button>
            <Stack spacing={2} alignItems="center" mb={4}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: alpha("#14b8a6", 0.15),
                }}
              >
                <ShieldOutlinedIcon sx={{ color: "#2dd4bf" }} />
              </Box>

              <Typography variant="h4" fontWeight={600} textAlign="center">
                Set Up Two-Factor Authentication
              </Typography>

              <Typography
                textAlign="center"
                sx={{ color: "#9ca3af", maxWidth: 420 }}
              >
                Add an extra layer of security to your account by enabling 2FA
              </Typography>
            </Stack>

            <Typography variant="h5" fontWeight={500} mb={2}>
              Choose your verification method
            </Typography>

            <Stack spacing={2} mb={3}>
              <MethodCard
                selected={method === "email"}
                icon={<EmailOutlinedIcon />}
                title="Email"
                subtitle="Receive codes via email"
                onClick={() => handleMethodChange("email")}
              />

              <MethodCard
                selected={method === "sms"}
                icon={<PhoneIphoneOutlinedIcon />}
                title="Mobile Phone"
                subtitle="Receive codes via SMS"
                onClick={() => handleMethodChange("sms")}
              />
            </Stack>

            <Box mb={3}>
              <Typography fontWeight={600} mb={1}>
                {method === "email" ? "Email Address" : "Mobile Number"}
              </Typography>
              <CustomTextField
                size="small"
                placeholder={
                  method === "email"
                    ? "Enter your email"
                    : "Enter your phone number"
                }
                controllerName="contactName"
                control={control}
                type={method === "email" ? "email" : "tel"}
                fullWidth
                required
                disabled
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                borderRadius: 1.5,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#169c9c" : "#c6ebeb",
                color: colors.grey[100],
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#169c9ce6" : "#b6e0e0",
                },
              }}
            >
              Send Verification Code
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate("/create_account_step", { state: { createPracticeAndUserRequest } })}
              sx={{
                mt: 2,
                textTransform: "none",
                color: "#2dd4bf",
              }}
            >
              Use a different email or mobile#
            </Button>

            <Typography
              variant="body2"
              textAlign="center"
              mt={3}
              sx={{ color: "#9ca3af" }}
            >
              Two-factor authentication helps protect your account from
              unauthorized access
            </Typography>
          </Card>
        </form>
      </Box>
    </Box>
  );
}

function MethodCard({
  selected,
  icon,
  title,
  subtitle,
  onClick,
}: {
  selected: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: 2,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        border: selected
          ? "1px solid #2dd4bf"
          : "1px solid rgba(255,255,255,0.08)",
        backgroundColor: selected ? "rgba(20,184,166,0.12)" : "",
        transition: "all .2s ease",
        "&:hover": {
          borderColor: "#2dd4bf",
        },
      }}
    >
      <Radio
        checked={selected}
        sx={{
          color: "#2dd4bf",
          "&.Mui-checked": { color: "#2dd4bf" },
        }}
      />

      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          background: "rgba(20,184,166,0.2)",
          color: "#2dd4bf",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "#9ca3af" }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}
